import React, { useState, useEffect } from 'react';
import App from './pages/App';
import Dashboard from './pages/Dashboard';
import AIAgentsPage from './pages/AIAgentsPage';
import OzonSuppliesPage from './pages/OzonSuppliesPage';
import LoginPage from './pages/LoginPage';
import SettingsPage from './pages/SettingsPage';
import { BillingPage } from './pages/BillingPage';
import { OnboardingPage } from './pages/OnboardingPage';
import TopNav from './components/TopNav';
import ErrorBoundary from './components/ErrorBoundary';
import {
  EntitlementProvider,
  useEntitlement,
} from './billing/EntitlementContext';
import { BillingStatusBanner } from './billing/BillingStatusBanner';
import { ModuleDisabledBanner } from './billing/ModuleDisabledBanner';
import { apiFetch } from './api';

const OWNER_LEVEL = 15;
const ADMIN_LEVEL = 10;
const MANAGER_LEVEL = 5;

const getRoleLevelFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return 0;
  const [, payload] = token.split('.');
  if (!payload) return 0;
  try {
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return Number(decoded.roleLevel || 0);
  } catch (error) {
    console.error(error);
    return 0;
  }
};

const getRoleLevel = () => {
  const stored = localStorage.getItem('userRoleLevel');
  if (stored !== null && stored !== undefined && stored !== '') {
    const numeric = Number(stored);
    if (!Number.isNaN(numeric)) {
      return numeric;
    }
  }
  const role = (localStorage.getItem('userRole') || '').toLowerCase();
  if (role === 'owner') return OWNER_LEVEL;
  if (role === 'admin') return ADMIN_LEVEL;
  if (role === 'manager') return MANAGER_LEVEL;
  if (role === 'user') return 1;
  const fromToken = getRoleLevelFromToken();
  if (fromToken) return fromToken;
  return 0;
};

type PageKey = 'dashboard' | 'ai_agents' | 'ozon_supplies' | 'tables' | 'settings' | 'billing';

const getInitialPage = (): PageKey => 'dashboard';

// `billing` shown to Owner + Admin per D25 (Admin views, Owner edits;
// the component itself enforces the POST/PUT role check server-side).
const getAllowedPages = (roleLevel: number): readonly PageKey[] => {
  if (roleLevel >= OWNER_LEVEL)
    return ['dashboard', 'ai_agents', 'ozon_supplies', 'tables', 'settings', 'billing'] as const;
  if (roleLevel >= ADMIN_LEVEL)
    return ['dashboard', 'ai_agents', 'ozon_supplies', 'tables', 'billing'] as const;
  if (roleLevel >= MANAGER_LEVEL) return ['dashboard', 'ai_agents', 'ozon_supplies'] as const;
  return ['dashboard', 'ai_agents', 'ozon_supplies'] as const;
};

const notifyStoreNameChanged = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('storeNameChanged'));
  }
};

/**
 * Wraps a page with an entitlement gate. When the module is disabled
 * we render ``ModuleDisabledBanner`` instead of the children — this
 * is the top-level pane gate (plan §8). Blocks inside a page that
 * need finer gating (e.g. the SPP competitor block inside the price
 * calculator) use ``useModule()`` directly.
 */
interface GateProps {
  module: 'ai' | 'supply' | 'spp';
  children: React.ReactNode;
}
function ModuleGate({ module, children }: GateProps) {
  const { isModuleEnabled: check } = useEntitlement();
  if (check(module)) return <>{children}</>;
  return <ModuleDisabledBanner module={module} />;
}

/**
 * Checks whether the current session is blocked by the Phase 9
 * onboarding wizard. Returns `{required: true}` on blocking states:
 *   * billing_state === 'PENDING_FIRST_PAYMENT' — user never paid; D23
 *     forces them to see /onboarding until payment lands.
 *   * `ONBOARDING_COMPLETED !== true` on a non-demo shop — the wizard
 *     hasn't been completed yet.
 * Returns `{required: false}` for demo shops, completed wizards, or
 * when we can't determine the state (fail-open so a broken admin
 * backend doesn't lock everyone out).
 */
async function resolveOnboardingGate(): Promise<{
  required: boolean;
  reason: 'pending_payment' | 'not_completed' | null;
}> {
  try {
    const response = await apiFetch('/api/onboarding/start');
    if (response.status === 403) return { required: false, reason: null };
    if (!response.ok) return { required: false, reason: null };
    const body = (await response.json()) as {
      billing_state?: string | null;
      completed?: boolean;
      is_demo?: boolean;
    };
    if (body.is_demo) return { required: false, reason: null };
    if (body.billing_state === 'PENDING_FIRST_PAYMENT') {
      return { required: true, reason: 'pending_payment' };
    }
    if (!body.completed) return { required: true, reason: 'not_completed' };
    return { required: false, reason: null };
  } catch {
    return { required: false, reason: null };
  }
}

const Root: React.FC = () => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [page, setPage] = useState<PageKey>(getInitialPage);
  const [settingsTab, setSettingsTab] = useState<'users' | 'notifications' | 'store' | 'products'>('users');
  const [showKeysModal, setShowKeysModal] = useState(false);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessError, setReadinessError] = useState('');
  const [onboardingRequired, setOnboardingRequired] = useState<boolean | null>(null);

  useEffect(() => {
    setLoggedIn(!!localStorage.getItem('token'));
  }, []);

  // Gate: check onboarding status on login. When required, the
  // wizard renders instead of the normal layout. We re-check on
  // login state change so logout → login flips the gate correctly.
  useEffect(() => {
    if (!loggedIn) {
      setOnboardingRequired(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const role = getRoleLevel();
      // Onboarding is owner-only (D25); non-owners can't complete it
      // so we let them into the app as they are — admins can still
      // add integrations under /settings.
      if (role < OWNER_LEVEL) {
        if (!cancelled) setOnboardingRequired(false);
        return;
      }
      const result = await resolveOnboardingGate();
      if (!cancelled) setOnboardingRequired(result.required);
    })().catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [loggedIn]);

  const checkSettingsReadiness = async () => {
    const roleLevel = getRoleLevel();
    if (roleLevel < OWNER_LEVEL) {
      setShowKeysModal(false);
      return;
    }
    setReadinessLoading(true);
    setReadinessError('');
    try {
      const res = await apiFetch('/api/settings/readiness');
      if (!res.ok) {
        throw new Error('Не удалось проверить настройки');
      }
      const data = await res.json();
      const missing: string[] = Array.isArray(data?.missing) ? data.missing : [];
      const missingStoreKeys = missing.includes('CLIENT_ID') || missing.includes('API_KEY');
      setShowKeysModal(missingStoreKeys && page !== 'settings');
    } catch (error) {
      setReadinessError(error instanceof Error ? error.message : 'Ошибка проверки настроек');
      setShowKeysModal(page !== 'settings');
    } finally {
      setReadinessLoading(false);
    }
  };

  useEffect(() => {
    if (!loggedIn) return;
    checkSettingsReadiness().catch(console.error);
  }, [loggedIn, page]);

  useEffect(() => {
    if (!loggedIn) {
      return;
    }
    const syncStoreName = async () => {
      try {
        const res = await apiFetch('/api/settings/store-name');
        if (!res.ok) {
          throw new Error(`store name: ${res.status}`);
        }
        const data = await res.json();
        const nextStoreName = String(data?.store_name || '').trim();
        if (nextStoreName) {
          localStorage.setItem('storeName', nextStoreName);
        } else {
          localStorage.removeItem('storeName');
        }
      } catch {
        localStorage.removeItem('storeName');
      }
      notifyStoreNameChanged();
    };
    syncStoreName().catch(console.error);
  }, [loggedIn]);

  const roleLevel = getRoleLevel();
  const allowedPages = getAllowedPages(roleLevel);

  useEffect(() => {
    if (!loggedIn) return;
    if (!allowedPages.includes(page)) {
      setPage(allowedPages[0]);
    }
  }, [allowedPages, page, loggedIn]);

  if (!loggedIn) {
    return (
      <LoginPage
        onLogin={() => {
          setLoggedIn(true);
          setPage(getInitialPage());
          setSettingsTab('users');
        }}
      />
    );
  }

  // Onboarding check is in flight → render a tiny spinner rather than
  // flashing the dashboard and then pulling it away.
  if (onboardingRequired === null) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
        Загрузка…
      </div>
    );
  }

  // Phase 9: force the onboarding wizard when the backend says so.
  // The wizard calls `onFinished` when the user reaches the `done`
  // step or clicks through to /billing; we then flip the gate off
  // and the normal layout mounts.
  if (onboardingRequired) {
    return (
      <ErrorBoundary>
        <OnboardingPage
          onFinished={() => setOnboardingRequired(false)}
        />
      </ErrorBoundary>
    );
  }

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userRoleLevel');
    localStorage.removeItem('storeName');
    notifyStoreNameChanged();
    setLoggedIn(false);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {showKeysModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="text-lg font-semibold text-slate-900 mb-2">
              Подключите ключи Ozon
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Для работы дашборда нужно заполнить <span className="font-semibold">Client ID</span> и{' '}
              <span className="font-semibold">API_KEY</span> в настройках магазина.
            </p>
            {readinessError && (
              <p className="mb-3 text-sm text-red-600">{readinessError}</p>
            )}
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                onClick={() => setShowKeysModal(false)}
                disabled={readinessLoading}
              >
                Отложить
              </button>
              <button
                type="button"
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
                onClick={() => {
                  setSettingsTab('store');
                  setPage('settings');
                  setShowKeysModal(false);
                }}
              >
                Перейти в настройки
              </button>
            </div>
          </div>
        </div>
      )}
      <TopNav active={page} onNavigate={setPage} onLogout={handleLogout} roleLevel={roleLevel} />
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 16px' }}>
        <BillingStatusBanner billingHref="#billing" />
      </div>
      {page === 'dashboard' && (
        <ErrorBoundary><Dashboard /></ErrorBoundary>
      )}
      {page === 'ai_agents' && (
        <ErrorBoundary>
          <ModuleGate module="ai"><AIAgentsPage /></ModuleGate>
        </ErrorBoundary>
      )}
      {page === 'ozon_supplies' && (
        <ErrorBoundary>
          <ModuleGate module="supply"><OzonSuppliesPage /></ModuleGate>
        </ErrorBoundary>
      )}
      {page === 'tables' && allowedPages.includes('tables') && (
        <ErrorBoundary><App /></ErrorBoundary>
      )}
      {page === 'settings' && allowedPages.includes('settings') && (
        <ErrorBoundary>
          <SettingsPage
            initialTab={settingsTab}
            onStoreSaved={() => checkSettingsReadiness().catch(console.error)}
          />
        </ErrorBoundary>
      )}
      {page === 'billing' && allowedPages.includes('billing') && (
        <ErrorBoundary><BillingPage /></ErrorBoundary>
      )}
    </div>
  );
};

// Wrapping the top-level Root with the EntitlementProvider means every
// downstream component can call `useEntitlement()` / `useModule()`
// without plumbing props. The provider itself skips its initial fetch
// while the user is logged out (via `disabled` prop) so the login
// page doesn't trigger a 401 loop.
const RootWithProvider: React.FC = () => {
  const [loggedIn, setLoggedIn] = useState<boolean>(
    () => !!localStorage.getItem('token'),
  );
  useEffect(() => {
    const onStorage = () => setLoggedIn(!!localStorage.getItem('token'));
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  return (
    <EntitlementProvider disabled={!loggedIn}>
      <Root />
    </EntitlementProvider>
  );
};

export default RootWithProvider;
